import { useCallback, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import fetchWrapper from "../../api/fetchWrapper";
import { ENDPOINT } from "../../api/endpoint";
import { setRefData } from "../../store/slices/filterSlice";
import { useDispatch } from "react-redux";

export const useGetRefData = (
  selectedRegions: string[] = [],
  refresh = false
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["refData"],
    queryFn: () => fetchWrapper(ENDPOINT.GET_REF_DATA, { method: "GET" }),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  useEffect(() => {
    if (refresh) {
      queryClient.invalidateQueries({
        queryKey: ["refData"],
      });
    }
  }, [refresh, queryClient]);

  const refData = query.data;

  useEffect(() => {
    if (refData) {
      dispatch(setRefData(refData));
    }
  }, [refData, dispatch]);

  const distinctRegions = useMemo(() => {
    return Array.from(
      new Set(
        refData?.["ref_country"]
          ?.map((item: any) => item.ts_region)
          .filter((region: string | undefined) => region?.trim())
      )
    );
  }, [refData]);

  const countryNames = useMemo(() => {
    return Array.from(
      new Set(
        refData?.["ref_country"]
          ?.map((item: any) => item.country_name)
          .filter(Boolean)
      )
    );
  }, [refData]);

  const regionAffiliateMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    refData?.["ref_country"]?.forEach((item: any) => {
      if (item.ts_region && item.country_name) {
        if (!map.has(item.ts_region)) {
          map.set(item.ts_region, new Set());
        }
        map.get(item.ts_region)?.add(item.country_name);
      }
    });
    return map;
  }, [refData]);

  const affiliateNames = useMemo(() => {
    if (!selectedRegions?.length) return [];
    const allAffiliates = new Set<string>();
    selectedRegions.forEach((region: string) => {
      const affiliates = regionAffiliateMap.get(region);
      affiliates?.forEach((a) => allAffiliates.add(a));
    });
    return Array.from(allAffiliates);
  }, [regionAffiliateMap, selectedRegions]);

  const { riskValues, riskLabels } = useMemo(() => {
    const values: string[] = [];
    const labels: Record<string, string> = {};
    refData?.["ref_risk_type"]?.forEach((item: any) => {
      if (item.risk_type_code && item.risk_type) {
        values.push(item.risk_type_code);
        labels[
          item.risk_type_code
        ] = `${item.risk_type_code} - ${item.risk_type}`;
      }
    });
    return { riskValues: values, riskLabels: labels };
  }, [refData]);

  const distinctEvaluations = useMemo(() => {
    return refData?.["ref_evaluation_type"]?.map(
      (item: any) => item.evaluation_type
    );
  }, [refData]);

  const distinctStatus = useMemo(() => {
    return refData?.["ref_status"]?.map(
      (item: any) => item.record_status
    );
  }, [refData]);

  const distinctProducts = useMemo(() => {
    return refData?.["ref_product"]?.map(
      (item: any) => item.product
    );
  }, [refData]);

  const distinctSampleId = useMemo(() => {
    return refData?.["sample_data"]?.map(
      (item: any) => item.sample_id
    );
  }, [refData]);

  const validAffiliates = useCallback(
    (affiliatesSelected: string[] = [], regionsSelected: string[] = []) => {
      const validAffiliates = new Set<string>();
      regionsSelected.forEach((region) => {
        const affiliates = regionAffiliateMap.get(region);
        affiliates?.forEach((a) => validAffiliates.add(a));
      });

      return affiliatesSelected.filter((a) => validAffiliates.has(a));
    },
    [regionAffiliateMap]
  );

  return {
    ...query,
    options: {
      distinctRegions,
      countryNames,
      affiliateNames,
      risks: { riskValues, riskLabels },
      distinctEvaluations,
      distinctStatus,
      distinctProducts,
      distinctSampleId,
      validAffiliates,
    },
  };
};
